/**
 * Pure helpers behind "@" mentions in task comments.
 *
 * Same reasoning as boardFilters.js: the composer lives inside TaskDetailModal,
 * which drags in Lexical, presence and live collab and cannot be exercised
 * cheaply — but the part that decides WHO gets notified must be tested, so it
 * lives here instead.
 *
 * The comment shape written by this module:
 *
 *   { id, body, createdAt, authorName, authorId, mentions: [userId, ...], authorAgent? }
 *
 * `authorAgent` (card 13eb6ec0) is set only on a comment an AI agent wrote
 * over MCP: one of COMMENT_AGENTS' ids, taken from the MCP client's identity
 * on the wire (Rust `client_agent.rs`) — never from a tool argument.
 *
 * `authorName` stays because legacy comments have only that; `authorId` and
 * `mentions` are new and are absent on every comment written before this.
 * Nothing here backfills — there is nothing to backfill from, a display name
 * cannot be resolved back to a user.
 */

/**
 * The "@…" being typed immediately left of the caret, or null.
 *
 * Anchored to a word boundary so an email address ("sam@x.com") in a comment
 * never opens the picker, and stopping at whitespace/@ so the query is one
 * token.
 */
export function findMentionQuery(textUpToCaret) {
  const m = /(?:^|\s)@([^\s@]*)$/.exec(String(textUpToCaret || ''));
  return m ? m[1] : null;
}

/**
 * Replace the "@…" left of `caret` with `@label ` and report where the caret
 * should land. Returns null when there is no "@" to complete.
 */
export function insertMention(draft, caret, label) {
  const text = String(draft || '');
  const pos = Math.max(0, Math.min(caret ?? text.length, text.length));
  const before = text.slice(0, pos);
  const at = before.lastIndexOf('@');
  if (at === -1) return null;
  const token = `@${label}`;
  return {
    text: `${before.slice(0, at)}${token} ${text.slice(pos)}`,
    caret: at + token.length + 1,
    token,
  };
}

/**
 * Ids to notify for a comment about to be written.
 *
 * `picked` is what the user actually chose from the picker in THIS draft, as
 * {id, token}. Resolution therefore happens at WRITE time, from a real
 * selection — never by re-parsing names out of the body when it is read.
 * Display names change and repeat: with two people called Sam, a name-based
 * read-time scheme notifies the wrong one and never says so.
 *
 * A pick whose inserted text is no longer in the body is dropped — typing
 * "@Sam" and then deleting it must not notify Sam.
 */
export function resolveMentions(body, picked) {
  const text = String(body || '');
  const ids = (picked || [])
    .filter((p) => p && p.token && text.includes(p.token))
    .map((p) => String(p.id));
  return [...new Set(ids)];
}

/** Agents a comment can name, and the name shown when the agent gave none. */
export const COMMENT_AGENTS = Object.freeze({
  claude: 'Claude',
  codex: 'Codex',
  cursor: 'Cursor',
  grok: 'Grok',
  gemini: 'Gemini',
});

const isAgentId = (id) => typeof id === 'string' && Object.prototype.hasOwnProperty.call(COMMENT_AGENTS, id);

/**
 * The agent behind a comment, or null for a human. A comment written before
 * `authorAgent` existed falls back on its name — but only an author-less one
 * (agents never carry an `authorId`), so a teammate called "Claude" stays a
 * person.
 */
export function commentAgentOf(comment) {
  if (!comment) return null;
  if (isAgentId(comment.authorAgent)) return comment.authorAgent;
  if (comment.authorId != null) return null;
  const name = String(comment.authorName || '').toLowerCase();
  return Object.keys(COMMENT_AGENTS).find((id) => name.includes(id)) || null;
}

/** The comment record itself. `authorId` is null, never undefined, for the
 *  usual reason: undefined is dropped by JSON on the way into the synced
 *  record, and a field that never leaves the device is not a field. */
export function buildComment({ id, body, authorName, authorId, picked, authorAgent = null, bodyHtml = null, now = new Date() }) {
  return {
    id,
    body,
    createdAt: now.toISOString(),
    authorName,
    authorId: authorId != null ? String(authorId) : null,
    mentions: resolveMentions(body, picked),
    ...(isAgentId(authorAgent) ? { authorAgent } : {}),
    // Rich comments (card 7de79076): the editor HTML beside the plain `body`
    // everything else reads. Only when the comment is more than plain words.
    ...(bodyHtml ? { bodyHtml } : {}),
  };
}
